import AwsS3Multipart from '@uppy/aws-s3-multipart'
import type { InternalMetadata } from '@uppy/core'
import Uppy from '@uppy/core'
import Tus from '@uppy/tus'
import { config } from '_appConfig'
import { sendTypedEvent } from 'analytics'
import React, { useMemo, useEffect } from 'react'

import type {
  FileMetaGroupAllQueryVariables,
  UpsertFileMetaMutationVariables,
} from 'gql/__generated__/graphql'
import type { UppyType } from 'store/models'

import { UppyHeicFix } from './customPluginsUppy/HeicFix'
import { PreUploadCustomPlugin } from './customPluginsUppy/PreUploadCustomPlugin'

export interface ITypeFileMeta extends InternalMetadata {
  dataVariables: UpsertFileMetaMutationVariables
  paramsUpdateCacheFiles: FileMetaGroupAllQueryVariables
  fileMetaId: string | undefined
  companyId?: string
  contentType?: string
  createdById: string
  projectId?: string
}

// for file size upload/uppy sync front/bec patch request upload-offset header https://tus.io/protocols/resumable-upload.html#patch
const CHUNK_SIZE = 8388608

const defaultContext = {} as UppyType

export const UppyContext = React.createContext(defaultContext)

/**
 * Get fetch options with authorization headers
 */
const getFetchOptions = (): RequestInit => ({
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UppyProvider = ({ children }: { children: any }) => {
  const uppy = useMemo(() => {
    const internalUppy = new Uppy({
      meta: {},
      autoProceed: true,
      allowMultipleUploads: true,
    })

    // @ts-expect-error TODO
    internalUppy.use(PreUploadCustomPlugin)

    // @ts-expect-error TODO
    internalUppy.use(UppyHeicFix)

    if (config.upload.useS3) {
      // TODO: Add proper error handling for S3 multipart fetch calls (createMultipartUpload, signPart, etc.)
      // S3 Multipart Upload mode
      internalUppy.use(AwsS3Multipart, {
        // Use our backend endpoints for S3 multipart upload
        createMultipartUpload: async (file) => {
          const response = await fetch(`${config.io.fileEndpoint}/v2/upload/init`, {
            method: 'PUT',
            ...getFetchOptions(),
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type,
              size: file.size,
              // Pass metadata from file.meta
              createdById: file.meta.createdById,
              fileMetaId: file.meta.fileMetaId,
              category: file.meta.category || 'image',
              name: file.meta.name,
              filetype: file.meta.contentType,
              companyId: file.meta.companyId,
              projectId: file.meta.projectId,
              employeeId: file.meta.employeeId,
              taskId: file.meta.taskId,
            }),
          })

          if (!response.ok) {
            throw new Error(`Failed to init upload: ${response.statusText}`)
          }

          const data = await response.json()
          return {
            uploadId: data.uploadId,
            key: data.objectKey,
          }
        },

        signPart: async (_file, { uploadId, key, partNumber }) => {
          // Parts URLs are already provided in init response
          // This is called per-part, so we need to get the presigned URL
          const url = new URL(
            `${config.io.fileEndpoint}/v2/upload/part/sign`,
            window.location.origin,
          )
          url.searchParams.set('objectKey', key)
          url.searchParams.set('uploadId', uploadId)
          url.searchParams.set('partNumber', String(partNumber))
          const response = await fetch(url.toString(), {
            method: 'GET',
            ...getFetchOptions(),
          })

          if (!response.ok) {
            throw new Error(`Failed to sign part: ${response.statusText}`)
          }

          const data = await response.json()
          return { url: data.url }
        },

        listParts: async (_file, { uploadId, key }) => {
          const url = new URL(
            `${config.io.fileEndpoint}/v2/upload/part/list`,
            window.location.origin,
          )
          url.searchParams.set('objectKey', key)
          url.searchParams.set('uploadId', uploadId)
          const response = await fetch(url.toString(), {
            method: 'GET',
            ...getFetchOptions(),
          })

          if (!response.ok) {
            throw new Error(`Failed to list parts: ${response.statusText}`)
          }

          const data = await response.json()
          return data.parts.map((p: { part: number; etag: string; size: number }) => ({
            PartNumber: p.part,
            ETag: p.etag,
            Size: p.size,
          }))
        },

        completeMultipartUpload: async (file, { uploadId, key, parts }) => {
          const url = new URL(
            `${config.io.fileEndpoint}/v2/upload/complete`,
            window.location.origin,
          )
          url.searchParams.set('objectKey', key)
          url.searchParams.set('uploadId', uploadId)
          const response = await fetch(url.toString(), {
            method: 'PATCH',
            ...getFetchOptions(),
            body: JSON.stringify({
              parts: parts.map((p) => ({
                part: p.PartNumber,
                etag: p.ETag,
              })),
              // Pass metadata from file.meta
              createdById: file.meta.createdById,
              fileMetaId: file.meta.fileMetaId,
              category: file.meta.category || 'image',
              name: file.meta.name,
              filetype: file.meta.contentType,
              companyId: file.meta.companyId,
              projectId: file.meta.projectId,
              employeeId: file.meta.employeeId,
              taskId: file.meta.taskId,
            }),
          })

          if (!response.ok) {
            throw new Error(`Failed to complete upload: ${response.statusText}`)
          }

          const data = await response.json()
          return { location: data.path }
        },

        abortMultipartUpload: async (_file, { uploadId, key }) => {
          const url = new URL(`${config.io.fileEndpoint}/v2/upload/abort`, window.location.origin)
          url.searchParams.set('objectKey', key)
          url.searchParams.set('uploadId', uploadId)
          await fetch(url.toString(), {
            method: 'DELETE',
            ...getFetchOptions(),
          })
        },
      })
    } else {
      // TUS Upload mode (legacy)
      internalUppy.use(Tus, {
        endpoint: config.io.fileEndpoint + '/upload',
        chunkSize: CHUNK_SIZE,
      })
    }

    return internalUppy
  }, [])

  useEffect(() => {
    return () => uppy.close()
  }, [uppy])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onComplete = (result: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.successful.forEach((file: any) => {
        const meta = file.meta as Record<string, unknown>
        if (meta?.fileMetaId && meta?.category !== 'font') {
          sendTypedEvent('resource.file.uploaded', {
            resourceId: String(meta.fileMetaId),
            resourceType: String(meta.contentType || ''),
            resourceLevel: meta.projectId ? 'project' : 'company',
          })
        }
      })
    }

    uppy.on('complete', onComplete)

    return () => {
      uppy.off('complete', onComplete)
    }
  }, [uppy])

  return <UppyContext.Provider value={uppy}>{children}</UppyContext.Provider>
}
